import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/csv";

export default function HistoryPage() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [startDT, setStartDT] = useState("");
  const [endDT, setEndDT] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reseller/recharges");
        setList(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = list.filter((r) => {
    if (filter && !r.recipient_uid.includes(filter)) return false;
    const t = new Date(r.created_at).getTime();
    if (startDT && t < new Date(startDT).getTime()) return false;
    if (endDT && t > new Date(endDT).getTime()) return false;
    return true;
  });
  const totalDiamonds = filtered
    .filter((r) => r.status === "success")
    .reduce((a, r) => a + (r.diamonds || 0), 0);

  const exportCsv = () => {
    const headers = ["Timestamp", "Platform", "Type", "Tier", "Mode", "Recipient UID", "Diamonds", "Balance after", "Status", "Note"];
    const rows = filtered.map((r) => [
      new Date(r.created_at).toLocaleString(),
      r.platform_name || "Trumeet",
      r.type === "noble" ? "Noble" : "Transfer",
      r.tier_label || "",
      r.mode || "",
      r.recipient_uid || "",
      r.diamonds ?? 0,
      r.balance_after ?? "",
      r.status || "",
      r.note || "",
    ]);
    downloadCsv(`recharge-history_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Records</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Recharge History</h1>
        <p className="text-zinc-500 mt-1">Every recharge you've processed, timestamped.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="history-filters">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by recipient UID"
          className="h-10 px-3 py-2 text-sm rounded-md border border-zinc-300 bg-white w-full sm:w-64"
          data-testid="history-filter"
        />
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">From</span>
          <input
            type="datetime-local"
            value={startDT}
            max={endDT || undefined}
            onChange={(e) => setStartDT(e.target.value)}
            className="h-10 px-2 text-sm rounded-md border border-zinc-300 bg-white"
            data-testid="history-start-date"
          />
          <span className="text-xs text-zinc-500">To</span>
          <input
            type="datetime-local"
            value={endDT}
            min={startDT || undefined}
            onChange={(e) => setEndDT(e.target.value)}
            className="h-10 px-2 text-sm rounded-md border border-zinc-300 bg-white"
            data-testid="history-end-date"
          />
          {(startDT || endDT) && (
            <button
              onClick={() => { setStartDT(""); setEndDT(""); }}
              className="h-10 px-3 rounded-md text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
              data-testid="history-clear-dates"
            >
              Clear
            </button>
          )}
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Diamonds sold (filtered)</div>
          <div className="font-display text-xl font-semibold tabular-nums font-mono text-zinc-900" data-testid="history-total-diamonds">
            {totalDiamonds.toLocaleString()}
          </div>
        </div>
        <button
          onClick={exportCsv}
          disabled={!filtered.length}
          data-testid="export-history-csv"
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> CSV
        </button>
      </div>


      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              {["Timestamp", "Platform", "Type", "Recipient UID", "Diamonds", "Balance after", "Status", "Note"].map((h) => (
                <th key={h} className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="8" className="px-6 py-12 text-center text-zinc-400 text-sm">{loading ? "Loading…" : "No recharges yet"}</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`history-row-${r.id}`}>
                <td className="px-6 py-3 text-sm text-zinc-700 font-mono">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-6 py-3 text-sm text-zinc-700">{r.platform_name || "Trumeet"}</td>
                <td className="px-6 py-3 text-sm">
                  {r.type === "noble" ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
                      {r.tier_label || "Noble"}{r.mode === "renew" ? " · renew" : ""}
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-500">Transfer</span>
                  )}
                </td>
                <td className="px-6 py-3 text-sm text-zinc-900 font-mono">{r.recipient_uid}</td>
                <td className="px-6 py-3 text-sm text-zinc-900 tabular-nums font-mono">{r.diamonds}</td>
                <td className="px-6 py-3 text-sm text-zinc-600 tabular-nums font-mono" data-testid={`history-balance-after-${r.id}`}>
                  {r.balance_after == null ? "—" : r.balance_after.toLocaleString()}
                </td>
                <td className="px-6 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                    r.status === "success" ? "bg-green-100 text-green-800" :
                    r.status === "failed" ? "bg-red-100 text-red-800" :
                    "bg-amber-100 text-amber-800"
                  }`}>{r.status}</span>
                </td>
                <td className="px-6 py-3 text-sm text-zinc-600">{r.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
